import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import LineChartToggleIcon from 'assets/images/linecharttoggle.svg';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { ObjectValues } from 'src/common/tsHelpers';

const containerWidth = 40;
const containerPadding = 2;
const iconContainerWidth = 20;
const translateBy = containerWidth - iconContainerWidth - 2 * containerPadding;

const StyledToggleContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: '#e5e5e5',
  padding: theme.spacing(containerPadding),
  borderRadius: theme.spacing(50),
  width: theme.spacing(containerWidth),
  cursor: 'pointer',
  boxSizing: 'border-box',
}));

const EmptyToggleContent = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
}));

const StyledIconContainer = styled('div')<{ isOn?: boolean }>(({ theme, isOn }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: isOn ? theme.palette.text.tertiary : theme.palette.white,
  padding: theme.spacing(2),
  borderRadius: '50%',
  width: theme.spacing(iconContainerWidth),
  height: theme.spacing(iconContainerWidth),
  boxShadow: `${isOn ? 1 : 0}px ${theme.spacing(2)} ${theme.spacing(5)} rgba(0,0,0,0.5)`,
  transform: `translate(${isOn ? theme.spacing(translateBy) : '0px'}) rotate(${
    isOn ? '29deg' : '0deg'
  })`,
  transition: '0.35s ease all',

  '& > svg': {
    color: isOn ? theme.palette.white : theme.palette.text.secondary,
    transition: '0.35s ease all',
  },
}));

export const ChartType = {
  Cumulative: 'cumulative',
  NonCumulative: 'noncumulative',
} as const;
export type ChartTypeValue = ObjectValues<typeof ChartType>;

type ChartTypeToggleProps = {
  isOn?: boolean;
  onToggle: () => void;
};

export const ChartTypeToggle = ({ isOn, onToggle }: ChartTypeToggleProps) => {
  const { t } = useTranslation();

  const title = isOn ? t('graphs.switchToCumulativeUsage') : t('graphs.switchToDailyUsage');

  return (
    <StyledToggleContainer onClick={onToggle} data-testid="chart-type-selector">
      <Tooltip showArrow={true} title={title} variant="dark">
        <EmptyToggleContent />
      </Tooltip>
      <StyledIconContainer isOn={isOn}>
        <LineChartToggleIcon />
      </StyledIconContainer>
    </StyledToggleContainer>
  );
};
